import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { backendApis, type AxonDevice } from '@/backendApis';
import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { electronApis } from '@/features/electron-api';
import { inBrowser } from '@/utils/capabilities';

export const axonDevicesKeys = {
  all: ['axon-devices'] as const,
};

const PLATFORMS: Record<string, { label: string; noun: string }> = {
  macos: { label: 'macOS', noun: 'Mac' },
  windows: { label: 'Windows', noun: 'PC' },
  ios: { label: 'iOS', noun: 'iPhone' },
  android: { label: 'Android', noun: 'phone' },
};

export const platformOf = (platform: string) =>
  PLATFORMS[platform] ?? { label: platform, noun: 'device' };

export const integrationsLabel = (count: number) =>
  `${count} ${count === 1 ? 'integration' : 'integrations'}`;

export function useAxonDevicesQuery(): UseQueryResult<AxonDevice[]> {
  return useQuery({
    queryKey: axonDevicesKeys.all,
    queryFn: async () => {
      // The web build has no device id, so no row is tagged as this device
      const currentDeviceId = inBrowser()
        ? undefined
        : ((await electronApis.system.getDeviceId()) ?? undefined);
      return backendApis.axonDevices.list(currentDeviceId);
    },
  });
}

export function useRevokeDeviceMutation(): UseMutationResult<
  void,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceSlug: string) =>
      backendApis.axonDevices.revoke(deviceSlug),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: axonDevicesKeys.all }),
  });
}

export function useRevokeDevice(): {
  revoke: (device: AxonDevice) => Promise<void>;
  revokingSlug: string | undefined;
} {
  const mutation = useRevokeDeviceMutation();
  const revoke = async (device: AxonDevice) => {
    const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
      title: `Revoke ${device.device_slug}?`,
      description: `Your assistant will stop using its ${integrationsLabel(device.integration_count)} right away. If you still use this ${platformOf(device.platform).noun}, they come back the next time Littlebird opens on it.`,
      confirmText: 'Revoke',
      cancelText: 'Cancel',
      confirmVariant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await mutation.mutateAsync(device.device_slug);
      toast.success(`Revoked ${device.device_slug}`);
    } catch {
      toast.error(`Could not revoke ${device.device_slug}. Try again.`);
    }
  };
  return {
    revoke,
    revokingSlug: mutation.isPending ? mutation.variables : undefined,
  };
}
